import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { hubKeys } from '@/lib/query';

/**
 * The shared reads, each one hook so the sidebar's counts and a page's list are
 * the same cache entry rather than two requests for one answer.
 *
 * The 30s interval is the fallback behind the SSE stream, for both roles: an
 * admin streams the org-wide journal and a member streams their own slice of it,
 * and either stream invalidates these keys as events land. The timer is what
 * covers a connection that dropped without saying so.
 */
const LIVE = { refetchInterval: 30_000 } as const;

export function useStats(enabled = true) {
  return useQuery({ queryKey: hubKeys.stats, queryFn: api.stats, enabled, ...LIVE });
}

export function useReview(enabled = true) {
  return useQuery({ queryKey: hubKeys.review, queryFn: api.review, enabled, ...LIVE });
}

/**
 * The window view, as narrow as the reader asked for. Keyed by window length
 * and by both filters, so switching 7 to 30 days or hopping between two
 * projects keeps every answer cached rather than refetching the one just left.
 *
 * `placeholderData` holds the last slice on screen while the next one loads:
 * picking a project should redraw twenty tiles, not blank them, and it matters
 * more here than on the window buttons because a filter is the control a reader
 * tries several of in a row.
 */
export function useMetrics(days: number, repo?: string | null, person?: string | null) {
  return useQuery({
    queryKey: hubKeys.metrics(days, repo, person),
    queryFn: () => api.metrics(days, { repo, person }),
    placeholderData: (previous) => previous,
    ...LIVE,
  });
}

/**
 * The device list, which is an admin read on the server since the admin
 * boundary moved into the guards. Callers that draw it beside member content
 * pass `enabled: false` for a member rather than letting the query earn a 403;
 * `AdminOnly` is the one caller that wants the 403, because the refusal is what
 * it renders.
 */
export function useWorkers(enabled = true) {
  return useQuery({
    queryKey: hubKeys.workers,
    queryFn: async () => (await api.workers()).workers,
    enabled,
    ...LIVE,
  });
}

/**
 * People, with their devices and account windows. Admin-only on the server, and
 * `enabled` for the same reason `useWorkers` has it: a caller that draws people
 * beside member content passes false rather than letting the query earn a 403,
 * while `AdminOnly` leaves it true because the refusal is what it renders.
 */
export function useUsers(enabled = true) {
  return useQuery({
    queryKey: hubKeys.users,
    queryFn: async () => (await api.users()).users,
    enabled,
    ...LIVE,
  });
}

/**
 * Every invite, open and closed. Admin-only on the server, like the people
 * behind them. People splits the list: what can still be redeemed on top, what
 * became of the rest underneath.
 */
export function useInvites() {
  return useQuery({
    queryKey: hubKeys.invites,
    queryFn: async () => (await api.invites()).invites,
    ...LIVE,
  });
}

export function useBriefs(enabled = true) {
  return useQuery({
    queryKey: hubKeys.briefs,
    queryFn: async () => (await api.briefs()).briefs,
    enabled,
    ...LIVE,
  });
}

export function useTasks(enabled = true) {
  return useQuery({
    queryKey: hubKeys.tasks,
    queryFn: async () => (await api.tasks()).tasks,
    enabled,
    ...LIVE,
  });
}

/**
 * The projects rollup. The sidebar lists repos, the Inbox summarises them and the
 * command palette jumps to them, so this stopped being one strip's private query
 * and became a shared one.
 */
export function useProjects(enabled = true) {
  return useQuery({
    queryKey: hubKeys.projects,
    queryFn: async () => (await api.projects()).projects,
    enabled,
    ...LIVE,
  });
}

/**
 * The repo registry (issue #101). Any signed-in caller may read it - the job
 * composer's picker needs the same list the admin panel manages - so unlike
 * `useWorkers`/`useUsers` this carries no `enabled` gate of its own.
 */
export function useRepos() {
  return useQuery({
    queryKey: hubKeys.repos,
    queryFn: async () => (await api.repos()).repos,
    ...LIVE,
  });
}

/**
 * The fleet-wide standing instructions. Admin-only on the server and read by
 * exactly one editor, so it is not on the live interval: it changes when an
 * admin changes it, and the write invalidates this key itself.
 */
export function useFleetPrompt() {
  return useQuery({
    queryKey: hubKeys.fleetPrompt,
    queryFn: async () => (await api.fleetPrompt()).prompt,
  });
}

export function useHealth() {
  return useQuery({ queryKey: hubKeys.health, queryFn: api.health, staleTime: 60_000 });
}

/**
 * The org-wide journal, as the audit log reads it.
 *
 * Admin-only on the server, and left enabled for a member on purpose: it is the
 * gate `AdminOnly` renders the refusal from, exactly as `useWorkers` and
 * `useUsers` are on their own pages.
 */
export function useEventsFeed() {
  return useQuery({
    queryKey: hubKeys.events,
    queryFn: async () => (await api.events(300)).events,
    ...LIVE,
  });
}

/**
 * The schedules and their next runs. A read every signed-in member may make,
 * like the jobs list: what the fleet does unattended at 02:00 is not a secret
 * from the people whose laptops do it.
 */
export function useSchedules(enabled = true) {
  return useQuery({
    queryKey: hubKeys.schedules,
    queryFn: async () => (await api.schedules()).schedules,
    enabled,
    ...LIVE,
  });
}

export function useSchedule(id: string | undefined) {
  return useQuery({
    queryKey: hubKeys.schedule(id ?? ''),
    queryFn: () => api.schedule(id!),
    enabled: Boolean(id),
    ...LIVE,
  });
}

/**
 * Composer starting points, and the hub's own zone. Both change only when the
 * hub is reconfigured, and the zone matters: a schedule fires in the hub's zone
 * and the browser's is very often a different one.
 */
export function useSchedulePresets(enabled = true) {
  return useQuery({
    queryKey: hubKeys.schedulePresets,
    queryFn: api.schedulePresets,
    enabled,
    staleTime: 5 * 60_000,
  });
}
